import { logoRemederi } from '@cannect/assets/img/brands'
import { faviconAspaec, faviconVikura } from '@cannect/assets/logos'

export type BrandKey = 'cannect' | 'aspaec' | 'remederi' | 'vikura'

export interface BrandSchedulingConfig {
  path: string | null

  originId: number | null
  priceField: 'online_appointment_value' | 'association_appointment_value' | 'remederi_appointment_value'

  themeClass?: string

  origin?: string

  hasTriage?: boolean

  hasInstantConsultation?: boolean

  /** Exibe "Já tenho uma consulta" na identificação: busca a última consulta
   * do paciente por email + CPF (rota anônima, resposta mínima). */
  hasAppointmentLookup?: boolean
}

export interface BrandMetaConfig {
  title: string
  description?: string
  favicon?: string
  themeColor?: string
}

export interface BrandConfig {
  key: BrandKey
  name: string
  domains: string[]
  pathPrefix?: string
  meta?: BrandMetaConfig
  colors: {
    primary: string
    secondary: string
    accent: string
    background: string
    text: string
    error: string
    success: string
  }
  logo: {
    main: string
    white?: string
    dark?: string
  }
  headerBackground?: string
  headerForeground?: string
  features: {
    showHeader: boolean
    showFooter: boolean
    showWhatsapp: boolean
    useAssociationHeader: boolean
  }
  routes: {
    home: string
    login: string
    register: string
  }
  scheduling: BrandSchedulingConfig
}

export const BRANDS_CONFIG: Record<BrandKey, BrandConfig> = {
  cannect: {
    key: 'cannect',
    name: 'Cannect',
    domains: ['cannect.life', 'www.cannect.life', 'localhost:8080'],
    colors: {
      primary: '#383F27',
      secondary: '#059669',
      accent: '#34D399',
      background: '#FFFFFF',
      text: '#1F2937',
      error: '#EF4444',
      success: '#10B981'
    },
    logo: {
      main: '/logo-cannect.svg',
      white: '/logo-cannect-white.svg'
    },
    features: {
      showHeader: true,
      showFooter: true,
      showWhatsapp: true,
      useAssociationHeader: false
    },
    routes: {
      home: '/',
      login: '/login',
      register: '/cadastro'
    },
    scheduling: {
      path: '/agendamento',
      originId: null,
      priceField: 'online_appointment_value'
    }
  },
  aspaec: {
    key: 'aspaec',
    name: 'ASPAEC',
    domains: ['aspaec.com.br', 'www.aspaec.com.br', 'acesso.aspaec.com.br', 'marca.localhost', 'aspaec.localhost'],
    pathPrefix: '/aspaec',
    meta: {
      title: 'ASPAEC — Associação de Pacientes',
      description: 'Portal de acesso da ASPAEC: agendamento de consultas e acompanhamento de pacientes.',
      favicon: faviconAspaec,
      themeColor: '#02B0BB'
    },
    colors: {
      primary: '#02B0BB',
      secondary: '#028A93',
      accent: '#4FD3D7',
      background: '#F9FAFB',
      text: '#111827',
      error: '#DC2626',
      success: '#059669'
    },
    logo: {
      main: '/logo-aspaec.svg',
      white: '/logo-aspaec-white.svg'
    },
    features: {
      showHeader: true,
      showFooter: false,
      showWhatsapp: true,
      useAssociationHeader: true
    },
    routes: {
      home: '/',
      login: '/login',
      register: '/cadastro'
    },
    scheduling: {
      path: '/aspaec/agendamento',
      originId: 3,
      priceField: 'association_appointment_value',
      themeClass: 'aspaec-theme'
    }
  },
  remederi: {
    key: 'remederi',
    name: 'Remederi',
    domains: ['remederi.com.br', 'www.remederi.com.br', 'remederi.localhost'],
    pathPrefix: '/remederi',
    meta: {
      title: 'Remederi — Agendamento de Consultas',
      description: 'Agende sua consulta e acompanhe seu tratamento pelo portal Remederi.',
      favicon: logoRemederi,
      themeColor: '#11BDC2'
    },
    colors: {
      primary: '#11BDC2',
      secondary: '#0E9BA0',
      accent: '#4FD3D7',
      background: '#FFFFFF',
      text: '#1F2937',
      error: '#DC2626',
      success: '#10B981'
    },
    logo: {
      main: '/logo-remederi.png'
    },
    headerForeground: '#1F2937',
    headerBackground: '#FFFFFF',
    features: {
      showHeader: true,
      showFooter: false,
      showWhatsapp: true,
      useAssociationHeader: true
    },
    routes: {
      home: '/remederi/agendamento',
      login: '/login',
      register: '/cadastro'
    },
    scheduling: {
      path: '/remederi/agendamento',
      originId: 4,
      priceField: 'remederi_appointment_value',
      themeClass: 'remederi-theme',
      hasTriage: true,
      hasInstantConsultation: true
    }
  },
  vikura: {
    key: 'vikura',
    name: 'Vikura',
    domains: ['vikura.com.br', 'www.vikura.com.br', 'agendamento.vikura.com.br', 'vikura.localhost'],
    pathPrefix: '/vikura',
    meta: {
      title: 'Vikura — Agendamento de Consultas',
      description: 'Agende sua consulta e acompanhe seu tratamento pelo portal Vikura.',
      favicon: faviconVikura,
      themeColor: '#FC5E3B'
    },
    colors: {
      primary: '#FC5E3B',
      secondary: '#E04A29',
      accent: '#FF8163',
      background: '#FFFFFF',
      text: '#1F2937',
      error: '#DC2626',
      success: '#10B981'
    },
    logo: {
      main: '/logo-vikura.png'
    },
    headerForeground: '#1F2937',
    headerBackground: '#FFFFFF',
    features: {
      showHeader: true,
      showFooter: false,
      showWhatsapp: true,
      useAssociationHeader: true
    },
    routes: {
      home: '/vikura/agendamento',
      login: '/login',
      register: '/cadastro'
    },
    scheduling: {
      path: '/vikura/agendamento',
      originId: 4,
      priceField: 'remederi_appointment_value',
      themeClass: 'vikura-theme',
      origin: 'remederi',
      hasTriage: true,
      hasInstantConsultation: true,
      hasAppointmentLookup: true
    }
  }
}

export function detectBrandFromHostname(hostname: string, pathname?: string): BrandKey {
  const cleanHostname = hostname.split(':')[0].toLowerCase()
  const cleanPathname = pathname?.toLowerCase() || ''

  if (cleanPathname.startsWith('/aspaec')) {
    return 'aspaec'
  }
  if (cleanPathname.startsWith('/remederi')) {
    return 'remederi'
  }
  if (cleanPathname.startsWith('/vikura')) {
    return 'vikura'
  }

  if (cleanHostname.includes('aspaec') || cleanHostname.includes('marca')) {
    return 'aspaec'
  }
  if (cleanHostname.includes('remederi')) {
    return 'remederi'
  }
  if (cleanHostname.includes('vikura')) {
    return 'vikura'
  }

  if (cleanHostname.includes('cannect') || cleanHostname.includes('localhost') || cleanHostname === '') {
    return 'cannect'
  }

  return 'cannect'
}

export function getBrandConfig(brandKey: BrandKey): BrandConfig {
  return BRANDS_CONFIG[brandKey]
}

/**
 * Marcas whitelabel cujo funil é 100% anônimo — não existe tela de login.
 *
 * O paciente só é autenticado no meio do funil, de forma transparente, pelo
 * `POST /register-or-retrieve` do cadastro simplificado. Antes disso (e sempre
 * que aquele token for rejeitado) o fluxo precisa seguir anônimo: não há para
 * onde redirecionar, e mandar para o login da Cannect quebra o whitelabel.
 */
const ANONYMOUS_BRAND_KEYS: BrandKey[] = ['remederi', 'vikura']

/**
 * `true` quando a navegação atual pertence a uma marca whitelabel sem login.
 *
 * Considera path (`/vikura/...`) e hostname (`vikura.com.br`), porque as duas
 * formas de acesso existem em produção — checar só o prefixo de path deixaria
 * o domínio próprio de fora.
 */
export function isAnonymousBrandFlow(hostname: string, pathname: string): boolean {
  return ANONYMOUS_BRAND_KEYS.includes(detectBrandFromHostname(hostname, pathname))
}

/**
 * Fluxo whitelabel anônimo da navegação corrente. Wrapper de `window` para os
 * pontos que rodam fora do React (interceptor do axios) e não têm o contexto
 * de marca disponível.
 */
export function isCurrentPathAnonymousBrandFlow(): boolean {
  if (typeof window === 'undefined') return false
  return isAnonymousBrandFlow(window.location.hostname, window.location.pathname)
}

/** Marca com funil anônimo (Remederi/Vikura) — ver `ANONYMOUS_BRAND_KEYS`. */
export function isAnonymousBrand(brandKey: BrandKey): boolean {
  return ANONYMOUS_BRAND_KEYS.includes(brandKey)
}

/**
 * Home do fluxo da marca. Cannect = '/'; marcas whitelabel = página de
 * agendamento da marca. Botões de "voltar ao início" dentro dos fluxos de
 * agendamento/consulta na hora devem usar isto em vez de '/' fixo — senão o
 * paciente da marca cai na home da Cannect.
 */
export function getBrandHomePath(config: BrandConfig): string {
  if (config.key === 'cannect') return '/'
  return config.scheduling.path ?? config.routes.home
}

/** Path com o prefixo da marca ('/consulta-na-hora' → '/vikura/consulta-na-hora'). */
export function getBrandPath(config: BrandConfig, subpath: string): string {
  return `${config.pathPrefix ?? ''}${subpath}`
}
